import { Box, Typography } from '@mui/material';
import { ReactNode } from 'react';

/**
 * Single-card table shell: header row + divided data rows, all inside one
 * bordered/rounded container (per Figma). Compose with DataTableHeader,
 * DataTableRow and DataTableCell.
 */
export function DataTable({ children }: { children: ReactNode }) {
  return (
    <Box
      sx={(theme) => ({
        borderRadius: 2,
        border: `1px solid ${theme.app.border}`,
        backgroundColor: theme.app.card,
        overflow: 'hidden',
      })}
    >
      {children}
    </Box>
  );
}

export function DataTableHeader({ children }: { children: ReactNode }) {
  return (
    <Box
      sx={(theme) => ({
        display: 'flex',
        alignItems: 'center',
        px: 3,
        py: 1.5,
        borderBottom: `1px solid ${theme.app.border}`,
      })}
    >
      {children}
    </Box>
  );
}

export function DataTableRow({
  children,
  onClick,
}: {
  children: ReactNode;
  onClick?: () => void;
}) {
  return (
    <Box
      onClick={onClick}
      sx={(theme) => ({
        display: 'flex',
        alignItems: 'center',
        px: 3,
        py: 2,
        borderBottom: `1px solid ${theme.app.border}`,
        cursor: onClick ? 'pointer' : 'default',
        '&:last-child': { borderBottom: 'none' },
        '&:hover': onClick ? { backgroundColor: theme.app.mutedBackground } : undefined,
      })}
    >
      {children}
    </Box>
  );
}

export function DataTableCell({
  flex,
  align = 'left',
  children,
}: {
  flex?: number | string;
  align?: 'left' | 'right' | 'center';
  children: ReactNode;
}) {
  return (
    <Box sx={{ flex: flex ?? 1, textAlign: align, minWidth: 0 }}>
      {children}
    </Box>
  );
}

export function DataTableHeaderCell({
  flex,
  align = 'left',
  children,
}: {
  flex?: number | string;
  align?: 'left' | 'right' | 'center';
  children: ReactNode;
}) {
  return (
    <DataTableCell flex={flex} align={align}>
      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
        {children}
      </Typography>
    </DataTableCell>
  );
}
